import { api } from './api';
import { logger } from '../utils/logger';
import { buildQueryString } from '../utils/query-string';
import type { ApiResponse } from '../types';
import type {
  Communication,
  CommunicationRecord,
  CommunicationDetails,
  CommunicationResult,
  CommunicationFilters,
  CommunicationStats,
  CommunicationTemplate,
  CreateEmailDraftRequest,
  UpdateEmailDraftRequest,
  SendImmediateEmailRequest,
  ScheduleCommunicationRequest,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  PaginatedResponse,
  Recipient
} from '../types/communication';

export class CommunicationService {
  private static instance: CommunicationService;

  public static getInstance(): CommunicationService {
    if (!CommunicationService.instance) {
      CommunicationService.instance = new CommunicationService();
    }
    return CommunicationService.instance;
  }

  // ============ DRAFT MANAGEMENT ============

  /**
   * Create a new communication draft
   */
  async createDraft(request: CreateEmailDraftRequest): Promise<Communication> {
    const response = await api.post<ApiResponse<Communication>>(
      '/communications/drafts',
      request
    );
    return response.data!;
  }

  /**
   * Get all drafts for the current user
   */
  async getDrafts(filters?: {
    type?: string;
    limit?: number;
    offset?: number;
  }): Promise<Communication[]> {
    const queryString = buildQueryString({
      type: filters?.type,
      limit: filters?.limit,
      offset: filters?.offset,
    });

    const response = await api.get<ApiResponse<Communication[]>>(
      `/communications/drafts${queryString}`
    );
    return response.data!;
  }

  /**
   * Get a specific draft by ID
   */
  async getDraft(id: string): Promise<CommunicationDetails> {
    const response = await api.get<ApiResponse<CommunicationDetails>>(
      `/communications/${id}`
    );
    return response.data!;
  }

  /**
   * Update an existing draft
   */
  async updateDraft(id: string, request: UpdateEmailDraftRequest): Promise<Communication> {
    const response = await api.put<ApiResponse<Communication>>(
      `/communications/drafts/${id}`,
      request
    );
    return response.data!;
  }

  /**
   * Delete a draft
   */
  async deleteDraft(id: string): Promise<void> {
    await api.delete(`/communications/drafts/${id}`);
  }

  // ============ SENDING & SCHEDULING ============

  /**
   * Send a communication immediately
   */
  async sendImmediate(request: SendImmediateEmailRequest): Promise<CommunicationResult> {
    const response = await api.post<ApiResponse<CommunicationResult>>(
      '/communications/send',
      request
    );
    return response.data!;
  }

  /**
   * Send an existing draft immediately
   */
  async sendDraft(id: string): Promise<CommunicationResult> {
    const response = await api.post<ApiResponse<CommunicationResult>>(
      `/communications/${id}/send`
    );
    return response.data!;
  }

  /**
   * Schedule a communication for later delivery
   */
  async scheduleCommunication(
    id: string,
    request: ScheduleCommunicationRequest
  ): Promise<CommunicationResult> {
    const response = await api.post<ApiResponse<CommunicationResult>>(
      `/communications/${id}/schedule`,
      request
    );
    return response.data!;
  }

  /**
   * Cancel a scheduled communication
   */
  async cancelScheduled(id: string): Promise<void> {
    await api.delete(`/communications/${id}/schedule`);
  }

  // ============ COMMUNICATION HISTORY ============

  /**
   * Get communication history with optional filters
   */
  async getCommunicationHistory(
    filters?: CommunicationFilters
  ): Promise<PaginatedResponse<CommunicationRecord>> {
    const queryString = buildQueryString({
      status: filters?.status?.length ? filters.status : undefined,
      type: filters?.type?.length ? filters.type : undefined,
      channel: filters?.channel?.length ? filters.channel : undefined,
      startDate: filters?.dateRange?.start,
      endDate: filters?.dateRange?.end,
      search: filters?.searchQuery,
      limit: filters?.limit,
      offset: filters?.offset,
    });

    const response = await api.get<ApiResponse<PaginatedResponse<CommunicationRecord>>>(
      `/communications${queryString}`
    );
    return response.data!;
  }

  /**
   * Get detailed communication information
   */
  async getCommunicationDetails(id: string): Promise<CommunicationDetails> {
    const response = await api.get<ApiResponse<CommunicationDetails>>(
      `/communications/${id}`
    );
    return response.data!;
  }

  /**
   * Get communication statistics
   */
  async getCommunicationStats(
    dateRange?: { start: string; end: string }
  ): Promise<CommunicationStats> {
    const queryString = buildQueryString({
      startDate: dateRange?.start,
      endDate: dateRange?.end,
    });

    const response = await api.get<ApiResponse<CommunicationStats>>(
      `/communications/stats${queryString}`
    );
    return response.data!;
  }

  /**
   * Resend a failed communication
   */
  async resendCommunication(id: string): Promise<CommunicationResult> {
    const response = await api.post<ApiResponse<CommunicationResult>>(
      `/communications/${id}/resend`
    );
    return response.data!;
  }

  // ============ ANALYTICS & FAILED COMMUNICATIONS ============

  /**
   * Get delivery analytics
   */
  async getDeliveryAnalytics(
    dateRange?: { start: string; end: string }
  ): Promise<any> {
    const queryString = buildQueryString({
      startDate: dateRange?.start,
      endDate: dateRange?.end,
    });

    const response = await api.get<ApiResponse<any>>(
      `/communications/analytics/delivery${queryString}`
    );
    return response.data!;
  }

  /**
   * Get time series data for analytics charts
   */
  async getTimeSeriesData(
    dateRange?: { start: string; end: string },
    groupBy: 'day' | 'week' | 'month' = 'day'
  ): Promise<any> {
    const queryString = buildQueryString({
      startDate: dateRange?.start,
      endDate: dateRange?.end,
      groupBy,
    });

    const response = await api.get<ApiResponse<any>>(
      `/communications/analytics/timeseries${queryString}`
    );
    return response.data!;
  }

  /**
   * Get individual delivery records for emails
   */
  async getDeliveryRecords(filters?: {
    dateRange?: { start: string; end: string };
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<any[]> {
    try {
      const queryString = buildQueryString({
        startDate: filters?.dateRange?.start,
        endDate: filters?.dateRange?.end,
        status: filters?.status,
        limit: filters?.limit,
        offset: filters?.offset,
      });

      const response = await api.get<ApiResponse<any[]>>(
        `/communications/delivery-records${queryString}`
      );
      
      // Handle the response structure properly
      // The API wrapper already extracts .data, so use response.data directly
      return response.data || [];
    } catch (error) {
      logger.error('❌ Error in getDeliveryRecords:', error);
      // Return empty array on error to prevent crashes
      return [];
    }
  }

  /**
   * Get failed communications
   */
  async getFailedCommunications(
    dateRange?: { start: string; end: string }
  ): Promise<any> {
    const queryString = buildQueryString({
      startDate: dateRange?.start,
      endDate: dateRange?.end,
    });

    const response = await api.get<ApiResponse<any>>(
      `/communications/failed${queryString}`
    );
    return response.data!;
  }

  /**
   * Get failed recipients for a specific communication
   */
  async getFailedRecipients(communicationId: string): Promise<any> {
    const response = await api.get<ApiResponse<any>>(
      `/communications/${communicationId}/recipients/failed`
    );
    return response.data!;
  }

  /**
   * Retry failed communication
   */
  async retryCommunication(id: string, recipientIds?: string[]): Promise<CommunicationResult> {
    const response = await api.post<ApiResponse<CommunicationResult>>(
      `/communications/${id}/retry`,
      recipientIds ? { recipientIds } : {}
    );
    return response.data!;
  }

  // ============ TEMPLATE MANAGEMENT ============

  /**
   * Get all communication templates
   */
  async getTemplates(filters?: {
    type?: string;
    channel?: string;
    isActive?: boolean;
  }): Promise<CommunicationTemplate[]> {
    const queryString = buildQueryString({
      type: filters?.type,
      channel: filters?.channel,
      isActive: filters?.isActive,
    });

    const response = await api.get<ApiResponse<{
      templates: CommunicationTemplate[];
      total: number;
      limit: number;
      offset: number;
    }>>(
      `/communications/templates${queryString}`
    );
    return response.data!.templates;
  }

  /**
   * Get a specific template by ID
   */
  async getTemplate(id: string): Promise<CommunicationTemplate> {
    const response = await api.get<ApiResponse<CommunicationTemplate>>(
      `/communications/templates/${id}`
    );
    return response.data!;
  }

  /**
   * Create a new template
   */
  async createTemplate(request: CreateTemplateRequest): Promise<CommunicationTemplate> {
    const response = await api.post<ApiResponse<CommunicationTemplate>>(
      '/communications/templates',
      request
    );
    return response.data!;
  }

  /**
   * Update an existing template
   */
  async updateTemplate(
    id: string,
    request: UpdateTemplateRequest
  ): Promise<CommunicationTemplate> {
    const response = await api.put<ApiResponse<CommunicationTemplate>>(
      `/communications/templates/${id}`,
      request
    );
    return response.data!;
  }

  /**
   * Delete a template
   */
  async deleteTemplate(id: string): Promise<void> {
    await api.delete(`/communications/templates/${id}`);
  }

  // ============ EVENT TICKET BUYERS ============

  /**
   * Get sellable event items for recipient selection
   */
  async getEvents(): Promise<Array<{ id: string; name: string; eventDate?: string | null; status: string }>> {
    const response = await api.get<ApiResponse<{
      items: Array<{ id: string; name: string; eventDate?: string | null; status: string; type: string }>;
      totalCount: number;
    }>>('/admin/ticketing/items?type=event_ticket');
    return (response.data?.items ?? []).filter(item => item.type === 'event_ticket');
  }

  /**
   * Get unique ticket buyers for a sellable item, formatted as recipients
   */
  async getEventBuyers(eventId: string): Promise<Recipient[]> {
    const response = await api.get<ApiResponse<{
      rows: Array<{ buyerEmail: string; buyerFirstName: string; buyerLastName: string }>;
    }>>(`/admin/ticketing/items/${eventId}/manifest`);

    const rows = response.data?.rows ?? [];
    const seen = new Set<string>();
    const recipients: Recipient[] = [];
    for (const row of rows) {
      if (row.buyerEmail && !seen.has(row.buyerEmail)) {
        seen.add(row.buyerEmail);
        recipients.push({
          id: `buyer-${row.buyerEmail}`,
          name: [row.buyerFirstName, row.buyerLastName].filter(Boolean).join(' ') || row.buyerEmail,
          email: row.buyerEmail,
          role: 'member',
          type: 'user'
        });
      }
    }
    return recipients;
  }

  /**
   * Preview a template with variables
   */
  async previewTemplate(
    id: string,
    variables: Record<string, any>
  ): Promise<{ subject: string; htmlContent: string; textContent: string }> {
    const response = await api.post<ApiResponse<{
      subject: string;
      htmlContent: string;
      textContent: string;
    }>>(`/communications/templates/${id}/preview`, { variables });
    return response.data!;
  }

  // ============ RECIPIENT MANAGEMENT ============

  /**
   * Get available recipients from family members
   */
  async getAvailableRecipients(query?: string): Promise<Recipient[]> {
    const queryString = buildQueryString({
      search: query,
      limit: 1000, // Get all members for recipient selection
    });

    const response = await api.get<ApiResponse<{
      members: any[];
      total: number;
      page: number;
      limit: number;
    }>>(
      `/family-members${queryString}`
    );

    const membersData = response.data!;
    // Transform family member data to Recipient format
    // Only include members with email addresses and who are active
    return membersData.members
      .filter((member: any) => member.email && member.isActive && !member.deathDate)
      .map((member: any) => ({
        id: member.id,
        name: member.nickname 
          ? `${member.firstName} "${member.nickname}" ${member.lastName}`.trim()
          : `${member.firstName} ${member.lastName}`.trim(),
        email: member.email,
        role: member.userId ? 'member' : 'guest', // Distinguish between users and family members
        type: member.userId ? 'user' : 'family_member'
      }));
  }

  /**
   * Get available recipients from user accounts only (verified emails)
   */
  async getAccountHolders(query?: string): Promise<Recipient[]> {
    const queryString = buildQueryString({
      search: query,
    });

    try {
      const response = await api.get<ApiResponse<{
        users: any[];
        total: number;
        page: number;
        limit: number;
      }>>(
        `/users${queryString}`
      );

      const usersData = response.data!;
      // Transform user data to Recipient format
      return usersData.users
        .filter((user: any) => user.emailVerified && user.isVerified)
        .map((user: any) => ({
          id: user.id,
          name: `${user.firstName} ${user.lastName}`.trim(),
          email: user.email,
          role: user.role?.toLowerCase() || 'member',
          type: 'user'
        }));
    } catch (error) {
      logger.warn('Failed to load account holders, falling back to family members with user accounts');
      
      // Fallback: get family members that have user accounts
      const allRecipients = await this.getAvailableRecipients(query);
      return allRecipients.filter(r => r.type === 'user');
    }
  }

  /**
   * Get recipient groups (e.g., "Account Holders", "All Family Members", etc.)
   */
  async getRecipientGroups(): Promise<Array<{
    id: string;
    name: string;
    description?: string;
    recipients: Recipient[];
  }>> {
    try {
      // Try to get predefined groups from backend if available
      const response = await api.get<any[]>('/communications/recipient-groups');
      return response;
    } catch (error) {
      // Fallback to creating groups dynamically from available data
      logger.warn('Recipient groups endpoint not available, creating dynamic groups');

      // Get both types of recipients
      const [familyDirectory, registeredMembers] = await Promise.all([
        this.getAvailableRecipients(),
        this.getAccountHolders().catch(() => []) // Don't fail if users endpoint doesn't exist
      ]);

      // Deduplicated union: registered members first, then family-directory-only contacts
      const registeredEmails = new Set(registeredMembers.map(r => r.email));
      const directoryOnly = familyDirectory.filter(r => !registeredEmails.has(r.email));
      const everyone = [...registeredMembers, ...directoryOnly];

      const groups = [
        {
          id: 'everyone',
          name: 'Everyone',
          description: 'All contacts — registered members and family directory (no duplicates)',
          recipients: everyone
        },
        {
          id: 'registered-members',
          name: 'Registered Members',
          description: 'People with a verified platform account',
          recipients: registeredMembers
        },
        {
          id: 'family-directory',
          name: 'Family Directory',
          description: 'Everyone in the family tree with an email (including non-registered family)',
          recipients: familyDirectory
        }
      ];

      // Add role-based groups if we have users with different roles
      const adminUsers = registeredMembers.filter(r => r.role === 'admin');
      const organizerUsers = registeredMembers.filter(r => r.role === 'organizer');

      if (adminUsers.length > 0) {
        groups.push({
          id: 'admins',
          name: 'Administrators',
          description: 'Family association administrators',
          recipients: adminUsers
        });
      }

      if (organizerUsers.length > 0) {
        groups.push({
          id: 'organizers',
          name: 'Organizers',
          description: 'Event organizers',
          recipients: organizerUsers
        });
      }

      return groups;
    }
  }
}

// Export singleton instance
export const communicationService = CommunicationService.getInstance();